import type { ServiceKey } from '@shared/api/config';

import { copy } from './copy';

/**
 * The nine sections, one per deployed backend service.
 *
 * **The count is read from the architecture rather than chosen.** Every service
 * owns its own `<service>__configurations` table, so «grouped by service» in the
 * catalog row resolves to one section per service that has one. Nine deployed
 * backends have one; three repos do not, and the page says so beneath the last
 * section rather than leaving the absence to be noticed.
 *
 * The **table names** below are the wire's own — `<service>__configurations` is
 * the naming convention the infrastructure standard fixes, and printing it is
 * how an operator knows which database row they are about to write. The
 * **headings and context lines** are this console's wording; nothing in either
 * is a configuration *value*, and no key is listed here: the rows come from each
 * service, which is the whole point of the surface.
 */
export interface ServiceSectionSpec {
  key: ServiceKey;
  /** The section's own `id`, so its heading and its table caption agree. */
  id: string;
  heading: string;
  /** `<service>__configurations` — the table this section reads. */
  table: string;
  /** What this service's settings govern, in the operator's terms. */
  context: string;
  caption: string;
}

export const SERVICE_SECTIONS: readonly ServiceSectionSpec[] = [
  {
    key: 'auth',
    id: 'cfg-auth',
    heading: copy.sections.auth.heading,
    table: 'backend-auth__configurations',
    context: copy.sections.auth.context,
    caption: copy.sections.auth.caption,
  },
  {
    key: 'content',
    id: 'cfg-content',
    heading: copy.sections.content.heading,
    table: 'backend-content__configurations',
    context: copy.sections.content.context,
    caption: copy.sections.content.caption,
  },
  {
    key: 'assets',
    id: 'cfg-assets',
    heading: copy.sections.assets.heading,
    table: 'backend-assets__configurations',
    context: copy.sections.assets.context,
    caption: copy.sections.assets.caption,
  },
  {
    key: 'search',
    id: 'cfg-search',
    heading: copy.sections.search.heading,
    table: 'backend-search__configurations',
    context: copy.sections.search.context,
    caption: copy.sections.search.caption,
  },
  {
    key: 'engagement',
    id: 'cfg-engagement',
    heading: copy.sections.engagement.heading,
    table: 'backend-engagement__configurations',
    context: copy.sections.engagement.context,
    caption: copy.sections.engagement.caption,
  },
  {
    key: 'analytics',
    id: 'cfg-analytics',
    heading: copy.sections.analytics.heading,
    table: 'backend-analytics__configurations',
    context: copy.sections.analytics.context,
    caption: copy.sections.analytics.caption,
  },
  {
    key: 'notification',
    id: 'cfg-notification',
    heading: copy.sections.notification.heading,
    table: 'backend-notification__configurations',
    context: copy.sections.notification.context,
    caption: copy.sections.notification.caption,
  },
  {
    key: 'finance',
    id: 'cfg-finance',
    heading: copy.sections.finance.heading,
    table: 'backend-finance__configurations',
    context: copy.sections.finance.context,
    caption: copy.sections.finance.caption,
  },
  {
    key: 'support',
    id: 'cfg-support',
    heading: copy.sections.support.heading,
    table: 'backend-support__configurations',
    context: copy.sections.support.context,
    caption: copy.sections.support.caption,
  },
];

/**
 * `/settings/<service>` — the address one service's table is edited at.
 *
 * **A PATH SEGMENT, NOT A QUERY PARAMETER.** The develop edge drops the query
 * string before a request reaches anything, so `?service=auth` survives a
 * client-side navigation and dies on the first reload or pasted link — which is
 * the one case a deep link exists for. A segment is carried by the history
 * fallback like every other route in this console.
 */
export function settingsPathFor(service: ServiceKey): string {
  return `/settings/${service}`;
}

/** The spec behind a URL segment, or `undefined` for a segment that names no service. */
export function serviceSectionFor(slug: string | undefined): ServiceSectionSpec | undefined {
  return SERVICE_SECTIONS.find((spec) => spec.key === slug);
}
